<template>
  <div
    v-if="libraryType"
    class="library"
  >
    <div class="library__body">
      <section class="main-content">
        <LibraryContentHeader
          :is-loading="isLoading"
          :sorting="sorting"
          :new-item-files="newItemFiles"
          :show-style-bulk-actions-toolbar="showStyleBulkActionsToolbar"
          :selected-style-ids="selectedStyleIds"
          :items-count="paging.itemsCount"
          :are-all-visible-selected="areAllVisibleSelected"
          @toggle-style-bulk-actions-toolbar="onToggleStyleBulkActionsToolbar"
          @toggle-select-visible-styles="onToggleSelectVisibleStyles"
          @clear-style-selection="clearSelectedStyleIds"
          @sorting-change="onSortingChange"
          @search-update="fetchData(items, fetchFilteredItems)"
          @bulk-delete="$emit('bulk-delete', $event)"
          @bulk-update-status="$emit('bulk-update-status', $event)"
        />

        <div class="main-content__workspace">
          <LibraryItems
            v-if="items"
            class="main-content__items"
            :items="items"
            :library-type="libraryType"
            :user="user"
            :loading="isLoading"
            :show-style-bulk-actions-toolbar="showStyleBulkActionsToolbar"
            :selected-style-ids="selectedStyleIds"
            :show-pagination="isPaginationMode"
            :paging="paging"
            @style-selection-change="onStyleSelectionChange"
            @page-change="onPageChange"
            @scroll-hit-bottom="onScrollHitBottom"
            @files-dropped="onFilesDropped"
          />
        </div>
      </section>

      <template v-if="detailId">
        <DetailStyle
          v-if="libraryType === LIBRARY_TYPE.STYLE"
          :item-id="detailId"
          @close="closeDetail"
        />
        <DetailBlock
          v-else-if="libraryType === LIBRARY_TYPE.BLOCK"
          :item-id="detailId"
          @close="closeDetail"
        />
        <DetailItem
          v-else
          :item-id="detailId"
        />
      </template>
      <CollapsibleAside
        class="library__aside-right-container"
        :default-collapsed="true"
        panel-name="Filters"
        expanded-width="256px"
        collapsed-width="44px"
      >
        <TheAsideRight
          class="library__aside-right"
          @filter-library="onFilterLibrary"
        />
      </CollapsibleAside>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { LIBRARY_TYPE } from '@/constants/libraryType'
import { FetchData } from '@/mixins/fetchData.js'
import Utils from '@/services/utils'
import { getFormConfig } from '@/services/formUtils'
import DetailItem from '@/components/library/DetailItem'
import LibraryContentHeader from '@/components/library/LibraryContentHeader'
import CollapsibleAside from '@/components/layout/CollapsibleAside.vue'

const INFINITE_SCROLL_PAGE_SIZE = 10
const BULK_SELECTION_PAGE_SIZE = 50

export default {
  name: 'ItemsLibrary',

  components: {
    DetailItem,
    LibraryContentHeader,
    CollapsibleAside
  },

  mixins: [FetchData],

  data () {
    return {
      LIBRARY_TYPE,
      detailId: null,
      newItemFiles: null,
      isLoading: false
    }
  },

  computed: {
    ...mapGetters([
      'getItems',
      'getSortingItems',
      'getAvailableFilters',
      'getItemDetail',
      'getPagingItems',
      'getSelectedStyleIds'
    ]),

    ...mapGetters({
      browzwearAPI: 'getBrowzwearAPI',
      user: 'getCognitoUserData',
      libraryType: 'getActiveLibraryType',
      activeTeam: 'getActiveTeam',
      showStyleBulkActionsToolbar: 'getStyleBulkSelectionMode'
    }),

    /**
     * @returns {object}
     */
    paging () {
      return (
        this.getPagingItems() || {
          currentPage: 1,
          itemsLimit: INFINITE_SCROLL_PAGE_SIZE,
          itemsCount: 0,
          mode: 'infinite'
        }
      )
    },

    /**
     * @returns {object[]}
     */
    items () {
      return this.getItems() || []
    },

    /**
     * @returns {number[]}
     */
    visibleStyleIds () {
      if (this.libraryType !== LIBRARY_TYPE.STYLE) {
        return []
      }

      return this.items
        .filter(item => item.libraryType === LIBRARY_TYPE.STYLE)
        .map(item => item.id)
        .filter(id => id !== null && id !== undefined)
    },

    /**
     * @returns {boolean}
     */
    areAllVisibleSelected () {
      return (
        this.visibleStyleIds.length > 0 &&
        this.visibleStyleIds.every(id => this.selectedStyleIds.includes(id))
      )
    },

    /**
     * @returns {boolean}
     */
    isPaginationMode () {
      return this.paging.mode === 'pagination'
    },

    /**
     * @returns {Array}
     */
    selectedStyleIds () {
      return this.getSelectedStyleIds || []
    },

    /**
     * @returns {object}
     */
    itemDetail () {
      return this.getItemDetail()
    },

    /**
     * @returns {Array}
     */
    sorting () {
      return this.getSortingItems()
    },

    /**
     * @returns {Array}
     */
    availableFilters () {
      return this.getAvailableFilters()
    },

    /**
     * @returns {Array}
     */
    camelCasedFilters () {
      return Utils.convertObjectKeysToCamelCase(this.availableFilters || {})
    },

    /**
     * @returns {object}
     */
    formConfiguration () {
      return getFormConfig(this.libraryType)
    }
  },

  watch: {
    /**
     * @param {string} to
     * @param {string} from
     */
    $route (to, from) {
      if (from.name !== to.name) {
        this.updateWorkspace()
      }

      if (from.query.group_id !== to.query.group_id) {
        this.fetchAvailableFilters()
      }

      this.parseUrlForDetail()
    },

    /**
     * @param {boolean} isActive
     * @param {boolean} wasActive
     */
    async showStyleBulkActionsToolbar (isActive, wasActive) {
      if (isActive === wasActive) {
        return
      }

      await this.updatePagingModeForSelection(isActive)

      if (!isActive && this.libraryType === LIBRARY_TYPE.STYLE) {
        this.isLoading = true

        try {
          this.resetItems({
            libraryType: this.libraryType,
            resetPage: false,
            resetCount: false
          })

          await this.fetchData(this.items, this.fetchFilteredItems)
        } finally {
          this.isLoading = false
        }
      }
    }
  },

  mounted () {
    this.updateWorkspace()
    this.parseUrlForDetail()
  },

  beforeDestroy () {
    if (this.showStyleBulkActionsToolbar) {
      this.setStyleBulkSelectionMode(false)
      this.clearSelectedStyleIds()
    }
  },

  methods: {
    ...mapActions([
      'setActiveLibraryType',
      'setSortingItems',
      'incrementPagingItems',
      'fetchFilteredItems',
      'fetchAvailableFilters',
      'fetchTags',
      'resetItems',
      'setWatched',
      'addSelectedStyleId',
      'removeSelectedStyleId',
      'clearSelectedStyleIds',
      'setPagingItemsPage',
      'setPagingItemsMode',
      'setPagingItemsLimit',
      'setSelectedStyleIds',
      'setStyleBulkSelectionMode'
    ]),

    /**
     * Enable or disable style bulk-selection mode.
     *
     * @param {boolean} isActive
     */
    onToggleStyleBulkActionsToolbar (isActive) {
      if (this.libraryType !== LIBRARY_TYPE.STYLE) {
        return
      }

      this.setStyleBulkSelectionMode(isActive)
    },

    /**
     * Switch between infinite scrolling and paginated bulk-selection mode
     * without refreshing the styles.
     *
     * @param {boolean} isActive
     */
    async updatePagingModeForSelection (isActive) {
      if (this.libraryType !== LIBRARY_TYPE.STYLE) {
        return
      }

      this.setPagingItemsMode({
        mode: isActive ? 'pagination' : 'infinite',
        libraryType: this.libraryType
      })

      this.setPagingItemsLimit({
        itemsLimit: isActive
          ? BULK_SELECTION_PAGE_SIZE
          : INFINITE_SCROLL_PAGE_SIZE,
        libraryType: this.libraryType
      })

      this.setPagingItemsPage({
        currentPage: 1,
        libraryType: this.libraryType
      })

      if (!isActive) {
        this.clearSelectedStyleIds()

        return
      }

      this.isLoading = true

      try {
        this.resetItems({
          libraryType: this.libraryType,
          resetPage: false,
          resetCount: false
        })

        await this.fetchData(this.items, this.fetchFilteredItems)
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Select or deselect every style visible on the current page.
     *
     * @param {boolean} selected
     */
    onToggleSelectVisibleStyles (selected) {
      if (selected) {
        const mergedIds = [
          ...new Set([...this.selectedStyleIds, ...this.visibleStyleIds])
        ]

        this.setSelectedStyleIds(mergedIds)

        return
      }

      const visibleIds = new Set(this.visibleStyleIds)

      this.setSelectedStyleIds(
        this.selectedStyleIds.filter(id => !visibleIds.has(id))
      )
    },

    /**
     * Load a specific pagination page.
     *
     * @param   {number}        currentPage
     *
     * @returns {Promise<void>}
     */
    async onPageChange (currentPage) {
      if (!this.isPaginationMode || currentPage === this.paging.currentPage) {
        return
      }

      this.isLoading = true

      this.setPagingItemsPage({
        currentPage,
        libraryType: this.libraryType
      })

      this.resetItems({
        libraryType: this.libraryType,
        resetPage: false,
        resetCount: false
      })

      try {
        await this.fetchData(this.items, this.fetchFilteredItems)
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Add or remove one style from the shared selection.
     *
     * @param {object}  selection
     * @param {number}  selection.id
     * @param {boolean} selection.selected
     */
    onStyleSelectionChange ({ id, selected }) {
      if (!this.showStyleBulkActionsToolbar) {
        return
      }

      if (selected) {
        this.addSelectedStyleId(id)

        return
      }

      this.removeSelectedStyleId(id)
    },

    /**
     */
    async updateWorkspace () {
      this.isLoading = true

      const newLibraryType = this.$router.currentRoute.meta.libraryType

      if (this.$route.name === 'LibraryStylesWatchlist') {
        this.setWatched({
          watched: true,
          libraryType: LIBRARY_TYPE.STYLE
        })
      } else {
        this.setWatched({
          watched: false,
          libraryType: LIBRARY_TYPE.STYLE
        })
      }

      if (
        newLibraryType !== LIBRARY_TYPE.STYLE &&
        this.showStyleBulkActionsToolbar
      ) {
        this.setStyleBulkSelectionMode(false)
        this.clearSelectedStyleIds()
      }

      this.setActiveLibraryType(newLibraryType)

      const shouldUsePagination =
        newLibraryType === LIBRARY_TYPE.STYLE &&
        this.showStyleBulkActionsToolbar

      this.setPagingItemsMode({
        mode: shouldUsePagination ? 'pagination' : 'infinite',
        libraryType: newLibraryType
      })

      this.setPagingItemsLimit({
        itemsLimit: shouldUsePagination
          ? BULK_SELECTION_PAGE_SIZE
          : INFINITE_SCROLL_PAGE_SIZE,
        libraryType: newLibraryType
      })

      this.setPagingItemsPage({
        currentPage: 1,
        libraryType: newLibraryType
      })

      this.resetItems({
        libraryType: newLibraryType
      })

      try {
        await this.fetchFilters()

        await this.fetchData(this.items, this.fetchFilteredItems)
      } finally {
        this.isLoading = false
      }

      if (this.libraryType === LIBRARY_TYPE.STYLE) {
        this.fetchTags({
          assignedOnly: true
        })
      }
    },

    /**
     */
    async fetchFilters () {
      if (!this.availableFilters) {
        await this.fetchAvailableFilters()
      }
    },

    /**
     * Apply filters and clear selections that may no longer be visible.
     *
     * @returns {Promise<void>}
     */
    async onFilterLibrary () {
      if (this.showStyleBulkActionsToolbar) {
        this.clearSelectedStyleIds()

        this.setPagingItemsPage({
          currentPage: 1,
          libraryType: this.libraryType
        })
      }

      this.resetItems({
        libraryType: this.libraryType,
        resetPage: !this.showStyleBulkActionsToolbar,
        resetCount: true
      })

      await this.fetchData(this.items, this.fetchFilteredItems)
    },

    /**
     */
    closeDetail () {
      if (!this.detailId) {
        // avoid duplicated navigation
        return
      }

      this.detailId = null
      const newQueryUrl = JSON.parse(JSON.stringify(this.$route.query))
      delete newQueryUrl.detail

      this.$router.replace({
        query: newQueryUrl
      })
    },

    /**
     */
    parseUrlForDetail () {
      this.detailId = this.$route.query.detail
        ? parseInt(this.$route.query.detail)
        : null
    },

    /**
     * @param {string} changedSorting
     */
    onSortingChange (changedSorting) {
      if (this.sorting === changedSorting) {
        return
      }

      this.setSortingItems({
        sorting: changedSorting
      })

      if (this.showStyleBulkActionsToolbar) {
        this.clearSelectedStyleIds()

        this.setPagingItemsPage({
          currentPage: 1,
          libraryType: this.libraryType
        })
      }

      this.fetchData(this.items, this.fetchFilteredItems, true)
    },

    /**
     */
    onScrollHitBottom () {
      if (this.isPaginationMode || this.isLoading) {
        return
      }

      this.incrementPagingItems()

      this.fetchData(this.items, this.fetchFilteredItems, true)
    },

    /**
     * Store files dropped onto the style library.
     *
     * @param {FileList|File[]} files
     */
    onFilesDropped (files) {
      if (this.showStyleBulkActionsToolbar) {
        return
      }

      this.newItemFiles = files
    }
  }
}
</script>

<style lang="scss" scoped>
$root-height: 100%;

.library {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: $root-height;
  min-height: 0;
}

.library__body {
  display: flex;
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  min-height: 0;
}

.main-content {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
  height: 100%;
  min-height: 0;
}

.main-content__workspace {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

.main-content__items {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  min-height: 0;
}

.library__aside-right-container {
  flex: 0 0 auto;
  align-self: stretch;
  height: 100%;
  padding: spacing(2) spacing(2) spacing(4) spacing(2);

  @media (max-width: $media-query-s) {
    display: none;
  }
}

.library__aside-right {
  align-self: flex-start;
  width: 100%;
  height: 100%;
  border: $border-divider;
  border-radius: spacing(1/2);
  box-shadow: $shadow-default;
}

.library__aside-right-container.collapsible-aside--collapsed {
  padding: spacing(2) 0 spacing(4) spacing(1);
}

.library__detail-item-dropdown {
  margin-left: spacing(2);
}

.library__detail-item-dropdown--danger {
  color: $red;

  &:hover {
    color: $red;
  }
}
</style>
